import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  ChevronRight,
  FileOutput,
  Loader2,
  PackageOpen,
  Percent,
  Plus,
  Search,
  Store,
  Trash2,
  Warehouse,
  X,
} from 'lucide-react';
import { api, ErroHttp } from '@/lib/api';
import { caixaAlta, cn } from '@/lib/utils';
import { useSessao } from '@/hooks/useSessao';
import { Botao } from '@/componentes/ui/botao';
import { Cartao, Esqueleto } from '@/componentes/ui/primitivos';
import { Rota } from './Rota';
import { haQuanto, moeda, type Destino, type ItemDaCarga, type NotaParaCarga, type PlanoCarga, type PreviaCarga, type ResumoCarga } from './tipos';

/**
 * Montar carga: várias NFs de compra num caminhão, cada item para um destino.
 *
 *   1. marca as notas (à esquerda, com o saldo de cada uma);
 *   2. distribui na grade: uma coluna por destino, uma linha por item de cada
 *      nota, e a coluna "Fica" mostra ao vivo o que sobra (o saldo da próxima);
 *      atalhos: "% do saldo" e "o resto" por destino, "nota inteira para…";
 *   3. embaixo, a NF de cada destino já calculada pelo servidor (a mesma conta
 *      da emissão) e o botão de emitir — uma NF por destino.
 * O rascunho se salva sozinho; quem abrir depois continua de onde parou.
 */

type Dist = Record<number, Record<number, number>>;

const n0 = (v: number): string => v.toLocaleString('pt-BR', { maximumFractionDigits: 2 });

function useAtrasado<T>(valor: T, ms: number): T {
  const [v, definir] = React.useState(valor);
  React.useEffect(() => {
    const t = setTimeout(() => definir(valor), ms);
    return () => clearTimeout(t);
  }, [valor, ms]);
  return v;
}

export function MontarCarga(): JSX.Element {
  const { temPermissao } = useSessao();
  const cliente = useQueryClient();
  const [params, definirParams] = useSearchParams();
  const cargaUrl = Number(params.get('carga')) || null;
  const podeMontar = temPermissao('entradas.agendar') || temPermissao('entradas.fiscal');

  const [cargaId, definirCargaId] = React.useState<number | null>(cargaUrl);
  const [nome, definirNome] = React.useState('');
  const [compras, definirCompras] = React.useState<number[]>([]);
  const [colunas, definirColunas] = React.useState<number[]>([]);
  const [dist, definirDist] = React.useState<Dist>({});
  const [carregada, definirCarregada] = React.useState<number | null>(null);

  const irPara = (id: number | null): void => {
    const novo = new URLSearchParams(params);
    if (id) novo.set('carga', String(id));
    else novo.delete('carga');
    definirParams(novo, { replace: true });
  };

  const cargas = useQuery<{ cargas: ResumoCarga[] }>({ queryKey: ['entradas-cargas'], queryFn: () => api.get('/api/entradas/cargas') });
  const destinosQ = useQuery<{ destinos: Destino[] }>({ queryKey: ['entradas-destinos'], queryFn: () => api.get('/api/entradas/destinos') });
  const atual = cargas.data?.cargas.find((c) => c.id === cargaId) ?? null;

  // Abrir uma carga salva: hidrata o estado uma vez por carga.
  React.useEffect(() => {
    if (!atual || carregada === atual.id) return;
    definirNome(atual.nome);
    definirCompras(atual.plano.compras);
    definirColunas(atual.plano.destinos.map((d) => d.destinoId));
    const d: Dist = {};
    for (const x of atual.plano.destinos) d[x.destinoId] = Object.fromEntries(x.linhas.map((l) => [l.nfCompraProduto, l.quantidade]));
    definirDist(d);
    definirCarregada(atual.id);
  }, [atual, carregada]);

  // Link para carga descartada (ou de outra base): começa uma nova em vez de ficar em branco.
  React.useEffect(() => {
    if (cargaId && cargas.data && !cargas.data.cargas.some((c) => c.id === cargaId) && carregada !== cargaId) novaCarga();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cargaId, cargas.data]);

  const novaCarga = (): void => {
    definirCargaId(null);
    definirCarregada(null);
    definirNome('');
    definirCompras([]);
    definirColunas([]);
    definirDist({});
    irPara(null);
  };

  const itensQ = useQuery<{ itens: ItemDaCarga[] }>({
    queryKey: ['entradas-carga-itens', [...compras].sort(), cargaId],
    queryFn: () => api.post('/api/entradas/carga/itens', { compras, cargaId }),
    enabled: compras.length > 0,
    placeholderData: (a) => a,
  });
  // O saldo de trabalho é o DISPONÍVEL (NF limitada pelo estoque do ERP); o da NF fica para mostrar.
  const itens = React.useMemo(
    () => (compras.length ? (itensQ.data?.itens ?? []) : []).map((i) => ({ ...i, saldoNf: i.saldo, saldo: Math.min(i.saldo, i.disponivel) })),
    [compras.length, itensQ.data],
  );

  const emitidos = new Set((atual?.nfs ?? []).filter((x) => x.status !== 'ERRO' && x.status !== 'CANCELADA').map((x) => x.destino));
  const encerrada = atual?.status === 'CONCLUIDA';
  const plano: PlanoCarga = React.useMemo(
    () => ({
      compras,
      destinos: colunas.map((id) => ({
        destinoId: id,
        linhas: Object.entries(dist[id] ?? {})
          .filter(([, q]) => q > 0)
          .map(([k, q]) => ({ nfCompraProduto: Number(k), quantidade: q })),
      })),
    }),
    [compras, colunas, dist],
  );
  const temAlgo = plano.destinos.some((d) => d.linhas.length);

  // ---- salvar sozinho (1,2 s depois da última mudança)
  const salvar = useMutation({
    mutationFn: (corpo: { id: number | null; nome: string; plano: PlanoCarga }) => api.put<{ id: number }>('/api/entradas/cargas', corpo),
    onSuccess: (r) => {
      if (!cargaId) {
        definirCargaId(r.id);
        definirCarregada(r.id);
        irPara(r.id);
      }
      void cliente.invalidateQueries({ queryKey: ['entradas-cargas'] });
      void cliente.invalidateQueries({ queryKey: ['entradas-carga-notas'] });
    },
  });
  const assinatura = JSON.stringify({ nome, plano });
  const assinaturaAtrasada = useAtrasado(assinatura, 1200);
  const ultimaSalva = React.useRef<string>('');
  React.useEffect(() => {
    if (!podeMontar || encerrada || !temAlgo) return;
    if (cargaId && carregada !== cargaId) return; // ainda hidratando
    if (assinaturaAtrasada === ultimaSalva.current || assinaturaAtrasada !== assinatura) return;
    ultimaSalva.current = assinaturaAtrasada;
    salvar.mutate({ id: cargaId, nome, plano });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assinaturaAtrasada]);
  const sujo = temAlgo && assinatura !== ultimaSalva.current && !(cargaId && carregada === cargaId && atual && JSON.stringify({ nome: atual.nome, plano: atual.plano }) === assinatura);

  // ---- prévia do servidor (NF de cada destino)
  const planoAtrasado = useAtrasado(plano, 500);
  const previa = useQuery<PreviaCarga>({
    queryKey: ['entradas-carga-previa', cargaId, planoAtrasado],
    queryFn: () => api.post('/api/entradas/carga/previa', { plano: planoAtrasado, cargaId }),
    enabled: planoAtrasado.destinos.some((d) => d.linhas.length),
    placeholderData: (a) => a,
  });

  const destinos = destinosQ.data?.destinos ?? [];
  const notasEscolhidas = compras;
  const empresaOrigem = React.useMemo(() => {
    const d = cliente.getQueryData<{ notas: NotaParaCarga[] }>(['entradas-carga-notas', '']);
    return d?.notas.find((n) => compras.includes(n.nfCompra))?.empresa ?? 11;
  }, [cliente, compras]);
  const destinosPossiveis = destinos.filter((d) => d.ativo && d.empresa !== empresaOrigem);

  // ---- operações da grade
  const restante = (i: ItemDaCarga, menos?: number): number => {
    const usado = colunas.filter((c) => !emitidos.has(c) && c !== menos).reduce((s, c) => s + (dist[c]?.[i.nfCompraProduto] ?? 0), 0);
    return Math.max(0, i.saldo - usado);
  };
  const definirQtd = (destino: number, item: number, q: number): void =>
    definirDist((d) => ({ ...d, [destino]: { ...d[destino], [item]: Math.max(0, q) } }));
  const preencher = (destino: number, alvo: ItemDaCarga[], f: (i: ItemDaCarga) => number): void =>
    definirDist((d) => {
      const col = { ...d[destino] };
      for (const i of alvo) col[i.nfCompraProduto] = Math.max(0, Math.min(f(i), restante(i, destino)));
      return { ...d, [destino]: col };
    });
  const adicionarColuna = (id: number): void => definirColunas((c) => (c.includes(id) ? c : [...c, id]));
  const tirarColuna = (id: number): void => {
    definirColunas((c) => c.filter((x) => x !== id));
    definirDist(({ [id]: _, ...resto }) => resto);
  };
  const alternarNota = (n: NotaParaCarga): void => {
    definirCompras((c) => (c.includes(n.nfCompra) ? c.filter((x) => x !== n.nfCompra) : [...c, n.nfCompra]));
  };

  const porNota = React.useMemo(() => {
    const m = new Map<number, ItemDaCarga[]>();
    for (const i of itens) m.set(i.nfCompra, [...(m.get(i.nfCompra) ?? []), i]);
    return [...m.entries()];
  }, [itens]);
  const totalColuna = (destino: number): { un: number; peso: number; valor: number } =>
    itens.reduce(
      (s, i) => {
        const q = dist[destino]?.[i.nfCompraProduto] ?? 0;
        return { un: s.un + q, peso: s.peso + q * (i.pesoUnitario ?? 0), valor: s.valor + q * (i.valorUnitario ?? 0) };
      },
      { un: 0, peso: 0, valor: 0 },
    );
  const sobraTotal = itens.reduce((s, i) => s + restante(i), 0);
  const saldoTotal = itens.reduce((s, i) => s + i.saldo, 0);
  const distribuidoTotal = saldoTotal - sobraTotal;

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(280px,340px)_1fr]">
      {/* ------------------------------------------------ coluna das notas */}
      {/* Coluna fixa na altura da tela; a lista de notas rola sozinha (não a página). */}
      <aside className="space-y-3 lg:sticky lg:top-[72px] lg:flex lg:h-[calc(100vh-88px)] lg:flex-col lg:self-start lg:space-y-0 lg:gap-3">
        <CargasSalvas
          cargas={cargas.data?.cargas ?? []}
          carregando={cargas.isLoading}
          atual={cargaId}
          destinos={destinos}
          aoAbrir={(c) => {
            definirCargaId(c.id);
            definirCarregada(null);
            ultimaSalva.current = JSON.stringify({ nome: c.nome, plano: c.plano });
            irPara(c.id);
          }}
          aoNova={novaCarga}
        />
        <Notas escolhidas={notasEscolhidas} empresa={compras.length ? empresaOrigem : null} aoAlternar={alternarNota} travado={encerrada || !podeMontar} cargaId={cargaId} />
      </aside>

      {/* ------------------------------------------------ distribuição */}
      <section className="min-w-0 space-y-4">
        <Cartao className="overflow-hidden">
          <div className="flex flex-wrap items-center gap-3 border-b border-borda px-4 py-3">
            <PackageOpen className="h-5 w-5 text-primaria" />
            {cargaId ? <span className="rounded-md bg-primaria-suave px-2 py-0.5 text-xs font-bold tabular-nums text-primaria">Carga #{cargaId}</span> : null}
            <input
              className="min-w-[180px] flex-1 rounded-md border border-transparent bg-transparent px-1 text-sm font-bold hover:border-borda focus:border-borda focus:outline-none"
              placeholder="Nome da carga (ex.: Bitrem Whirlpool 28/10)"
              value={nome}
              disabled={encerrada || !podeMontar}
              onChange={(e) => definirNome(e.target.value)}
            />
            <EstadoSalvo salvando={salvar.isPending} sujo={sujo} erro={salvar.error} id={cargaId} encerrada={encerrada} />
            {colunas.length < destinosPossiveis.length && compras.length && !encerrada ? (
              <AdicionarDestino destinos={destinosPossiveis.filter((d) => !colunas.includes(d.id))} aoEscolher={adicionarColuna} />
            ) : null}
          </div>

          {!compras.length ? (
            <Vazio />
          ) : itensQ.isLoading ? (
            <div className="space-y-2 p-4">
              <Esqueleto className="h-8" />
              <Esqueleto className="h-8" />
              <Esqueleto className="h-8" />
            </div>
          ) : (
            <>
              <Progresso distribuido={distribuidoTotal} saldo={saldoTotal} />
              <div className="max-h-[60vh] overflow-auto">
                <table className="w-full border-separate border-spacing-0 text-xs">
                  <thead className="sticky top-0 z-10 bg-superficie2">
                    <tr className="text-left text-2xs uppercase tracking-wide text-textoSuave">
                      <th className="sticky left-0 z-20 min-w-[220px] border-b border-borda bg-superficie2 px-3 py-2">Item</th>
                      <th className="border-b border-borda px-2 py-2 text-right">Saldo</th>
                      {colunas.map((c) => {
                        const d = destinos.find((x) => x.id === c);
                        return (
                          <th key={c} className="min-w-[132px] border-b border-l border-borda px-2 py-1.5 align-top normal-case tracking-normal">
                            <CabecaDestino
                              destino={d}
                              emitido={emitidos.has(c)}
                              travado={encerrada || !podeMontar}
                              aoPercentual={(pct) => preencher(c, itens, (i) => Math.floor((i.saldo * pct) / 100))}
                              aoResto={() => preencher(c, itens, (i) => (dist[c]?.[i.nfCompraProduto] ?? 0) + restante(i))}
                              aoLimpar={() => definirDist((x) => ({ ...x, [c]: {} }))}
                              aoTirar={() => tirarColuna(c)}
                            />
                          </th>
                        );
                      })}
                      <th className="min-w-[80px] border-b border-l border-borda px-2 py-2 text-right" title="O que não vai para destino nenhum: continua como saldo da nota">
                        Fica
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {porNota.map(([nf, linhas]) => (
                      <GrupoNota
                        key={nf}
                        linhas={linhas}
                        colunas={colunas}
                        destinos={destinos}
                        dist={dist}
                        emitidos={emitidos}
                        travado={encerrada || !podeMontar}
                        restante={restante}
                        aoMudar={definirQtd}
                        aoNotaInteira={(destino) => preencher(destino, linhas, (i) => (dist[destino]?.[i.nfCompraProduto] ?? 0) + restante(i))}
                      />
                    ))}
                  </tbody>
                  {colunas.length ? (
                    <tfoot className="sticky bottom-0 bg-superficie2 font-semibold">
                      <tr>
                        <td className="sticky left-0 border-t border-borda bg-superficie2 px-3 py-2">Total por destino</td>
                        <td className="border-t border-borda px-2 py-2 text-right tabular-nums">{n0(saldoTotal)}</td>
                        {colunas.map((c) => {
                          const t = totalColuna(c);
                          return (
                            <td key={c} className="border-l border-t border-borda px-2 py-2 text-right tabular-nums">
                              {n0(t.un)} un.
                              <span className="block text-2xs font-normal text-textoFraco">
                                {moeda(t.valor)} · {n0(Math.round(t.peso))} kg
                              </span>
                            </td>
                          );
                        })}
                        <td className="border-l border-t border-borda px-2 py-2 text-right tabular-nums">{n0(sobraTotal)}</td>
                      </tr>
                    </tfoot>
                  ) : null}
                </table>
              </div>
              {!colunas.length ? (
                <p className="border-t border-borda px-4 py-3 text-[13px] text-textoFraco">
                  Agora escolha para onde vai: <b>+ Destino</b>, no canto de cima. Cada destino vira uma coluna e uma NF de transferência.
                </p>
              ) : null}
            </>
          )}
        </Cartao>

        {colunas.length && temAlgo ? (
          <NfsDaCarga
            previa={previa.data}
            carregando={previa.isFetching || plano !== planoAtrasado}
            erro={previa.error}
            cargaId={cargaId}
            sujo={sujo}
            aoEmitida={() => {
              void cliente.invalidateQueries({ queryKey: ['entradas-cargas'] });
              void cliente.invalidateQueries({ queryKey: ['entradas-carga-itens'] });
              void cliente.invalidateQueries({ queryKey: ['entradas-carga-notas'] });
              void cliente.invalidateQueries({ queryKey: ['entradas-carga-previa'] });
            }}
          />
        ) : null}
      </section>
    </div>
  );
}

// ------------------------------------------------------------------ peças da grade

function Progresso({ distribuido, saldo }: { distribuido: number; saldo: number }): JSX.Element {
  const pct = saldo ? Math.min(100, (distribuido / saldo) * 100) : 0;
  return (
    <div className="flex items-center gap-3 border-b border-borda px-4 py-2 text-xs">
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-superficie2">
        <div className="h-full rounded-full bg-primaria transition-all duration-300" style={{ width: `${pct}%` }} />
      </div>
      <span className="shrink-0 tabular-nums text-textoFraco">
        <b className="text-texto">{n0(distribuido)}</b> de {n0(saldo)} un. distribuídas
        {saldo - distribuido > 0 ? ` · ${n0(saldo - distribuido)} ficam como saldo` : ' · tudo distribuído'}
      </span>
    </div>
  );
}

function GrupoNota({
  linhas,
  colunas,
  destinos,
  dist,
  emitidos,
  travado,
  restante,
  aoMudar,
  aoNotaInteira,
}: {
  linhas: ItemDaCarga[];
  colunas: number[];
  destinos: Destino[];
  dist: Dist;
  emitidos: Set<number>;
  travado: boolean;
  restante: (i: ItemDaCarga, menos?: number) => number;
  aoMudar: (destino: number, item: number, q: number) => void;
  aoNotaInteira: (destino: number) => void;
}): JSX.Element {
  const l0 = linhas[0]!;
  const abertas = colunas.filter((c) => !emitidos.has(c));
  return (
    <>
      <tr>
        <td colSpan={3 + colunas.length} className="sticky left-0 border-b border-borda bg-superficie px-3 pb-1 pt-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[13px] font-bold">NF {l0.numeroCompra ?? l0.nfCompra}</span>
            <span className="text-2xs text-textoSuave">NF de compra {l0.nfCompra} · {linhas.length} item(ns)</span>
            {!travado && abertas.length ? (
              <span className="flex flex-wrap items-center gap-1 text-2xs">
                <span className="text-textoSuave">nota inteira →</span>
                {abertas.map((c) => {
                  const d = destinos.find((x) => x.id === c);
                  return (
                    <button
                      key={c}
                      type="button"
                      className="rounded-full border border-borda px-2 py-0.5 font-semibold text-primaria hover:border-primaria hover:bg-primaria-suave"
                      onClick={() => aoNotaInteira(c)}
                    >
                      {d?.nome ?? c} {d?.uf}
                    </button>
                  );
                })}
              </span>
            ) : null}
          </div>
        </td>
      </tr>
      {linhas.map((i) => {
        const fica = restante(i);
        const usado = colunas.filter((c) => !emitidos.has(c)).reduce((s, c) => s + (dist[c]?.[i.nfCompraProduto] ?? 0), 0);
        const passou = usado > i.saldo;
        const outras = i.emOutrasCargas.reduce((s, x) => s + x.quantidade, 0);
        const conflita = !passou && usado > 0 && usado + outras > i.saldo;
        return (
          <tr key={i.nfCompraProduto} className="group hover:bg-superficie2/60">
            <td className="sticky left-0 border-b border-borda bg-superficie px-3 py-1.5 group-hover:bg-superficie2">
              <span className="font-semibold tabular-nums">{i.produto}</span>{' '}
              <span className="line-clamp-1 text-textoFraco" title={i.descricao ?? undefined}>
                {i.descricao}
              </span>
            </td>
            <td className="border-b border-borda px-2 py-1.5 text-right tabular-nums">
              {n0(i.saldo)}
              {i.saldo < (i.saldoNf ?? i.saldo) ? (
                <span className="block whitespace-nowrap text-2xs font-semibold text-status-divergencia" title={i.motivoEstoque ?? undefined}>
                  NF tem {n0(i.saldoNf ?? 0)} · {i.motivoEstoque}
                </span>
              ) : i.saldo < i.quantidadeNota ? (
                <span className="block text-2xs text-textoSuave">de {n0(i.quantidadeNota)}</span>
              ) : null}
              {i.emOutrasCargas.length ? (
                <span className="block whitespace-nowrap text-2xs font-semibold text-status-atencao" title="Já distribuído em outra carga aberta (rascunho não reserva). Clique para abrir.">
                  {i.emOutrasCargas.map((x, k) => (
                    <React.Fragment key={x.carga}>
                      {k ? ' · ' : ''}
                      <a href={`/entradas?aba=carga&carga=${x.carga}`} className="underline decoration-dotted hover:decoration-solid">
                        {n0(x.quantidade)} na carga #{x.carga}
                      </a>
                    </React.Fragment>
                  ))}
                </span>
              ) : null}
            </td>
            {colunas.map((c) => {
              const q = dist[c]?.[i.nfCompraProduto] ?? 0;
              const fechada = emitidos.has(c) || travado || (i.saldo === 0 && q === 0);
              return (
                <td key={c} className={cn('border-b border-l border-borda px-1.5 py-1', q > 0 && 'bg-primaria-suave/30')}>
                  <input
                    type="number"
                    min={0}
                    step="any"
                    inputMode="decimal"
                    disabled={fechada}
                    aria-label={`Quantidade do produto ${i.produto} para o destino ${c}`}
                    title={fechada ? undefined : 'Duplo clique: põe aqui tudo o que sobrou'}
                    className={cn(
                      'h-8 w-full rounded-md border bg-superficie px-2 text-right tabular-nums transition-colors focus:border-primaria focus:outline-none',
                      q > 0 ? 'border-primaria/50 font-semibold' : 'border-borda text-textoSuave',
                      passou && q > 0 && 'border-status-divergencia text-status-divergencia',
                      fechada && 'cursor-not-allowed opacity-70',
                    )}
                    value={q || ''}
                    placeholder="0"
                    onFocus={(e) => e.target.select()}
                    onDoubleClick={() => !fechada && aoMudar(c, i.nfCompraProduto, q + fica)}
                    onChange={(e) => aoMudar(c, i.nfCompraProduto, Number(e.target.value) || 0)}
                  />
                </td>
              );
            })}
            <td
              className={cn(
                'border-b border-l border-borda px-2 py-1.5 text-right font-semibold tabular-nums',
                passou ? 'text-status-divergencia' : conflita ? 'text-status-atencao' : fica === 0 ? 'text-status-livre' : 'text-textoFraco',
              )}
            >
              {conflita ? (
                <span title={`Somando com a(s) carga(s) ${i.emOutrasCargas.map((x) => x.carga).join(', ')}, passa do saldo em ${n0(usado + outras - i.saldo)}. Quem emitir por último é barrado.`}>
                  <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />
                  {n0(i.saldo - usado - outras)}
                </span>
              ) : passou ? (
                <span title="Distribuiu mais do que o saldo">
                  <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />
                  {n0(i.saldo - usado)}
                </span>
              ) : fica === 0 ? (
                <Check className="ml-auto h-4 w-4" aria-label="Tudo distribuído" />
              ) : (
                n0(fica)
              )}
            </td>
          </tr>
        );
      })}
    </>
  );
}

function CabecaDestino({
  destino,
  emitido,
  travado,
  aoPercentual,
  aoResto,
  aoLimpar,
  aoTirar,
}: {
  destino: Destino | undefined;
  emitido: boolean;
  travado: boolean;
  aoPercentual: (pct: number) => void;
  aoResto: () => void;
  aoLimpar: () => void;
  aoTirar: () => void;
}): JSX.Element {
  const [pct, definirPct] = React.useState('');
  const Icone = destino?.tipo === 'FULL' ? Store : Warehouse;
  return (
    <div className="space-y-1">
      <div className="flex items-start justify-between gap-1">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primaria text-white">
            <Icone className="h-3.5 w-3.5" />
          </span>
          <span className="min-w-0 leading-tight">
            <b className="block truncate text-xs">{destino?.nome ?? '…'}</b>
            <span className="text-2xs text-textoSuave">
              {destino?.uf} · emp. {destino?.empresa}
            </span>
          </span>
        </span>
        {!emitido && !travado ? (
          <button type="button" className="rounded p-0.5 text-textoSuave hover:bg-superficie hover:text-status-divergencia" title="Tirar este destino" onClick={aoTirar}>
            <X className="h-3.5 w-3.5" />
          </button>
        ) : null}
      </div>
      {emitido ? (
        <span className="inline-flex items-center gap-1 rounded-full bg-status-livreFundo px-2 py-0.5 text-2xs font-semibold text-status-livre">
          <CheckCircle2 className="h-3 w-3" /> NF emitida
        </span>
      ) : travado ? null : (
        <div className="flex items-center gap-1">
          <form
            className="flex items-center rounded-md border border-borda bg-superficie"
            onSubmit={(e) => {
              e.preventDefault();
              const v = Number(pct.replace(',', '.'));
              if (v > 0 && v <= 100) aoPercentual(v);
            }}
          >
            <input
              className="h-6 w-9 rounded-l-md bg-transparent px-1 text-right text-2xs focus:outline-none"
              placeholder="25"
              inputMode="decimal"
              aria-label="Percentual do saldo de cada item"
              value={pct}
              onChange={(e) => definirPct(e.target.value.replace(/[^\d,.]/g, ''))}
            />
            <button type="submit" className="flex h-6 items-center px-1 text-textoSuave hover:text-primaria" title="Pôr este % do saldo de cada item">
              <Percent className="h-3 w-3" />
            </button>
          </form>
          <button type="button" className="h-6 rounded-md border border-borda px-1.5 text-2xs font-semibold text-primaria hover:border-primaria" title="Pôr aqui tudo o que sobrou" onClick={aoResto}>
            resto
          </button>
          <button type="button" className="h-6 rounded-md px-1 text-2xs text-textoSuave hover:text-texto" title="Zerar esta coluna" onClick={aoLimpar}>
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      )}
    </div>
  );
}

function AdicionarDestino({ destinos, aoEscolher }: { destinos: Destino[]; aoEscolher: (id: number) => void }): JSX.Element {
  const [aberto, definirAberto] = React.useState(false);
  return (
    <div className="relative">
      <Botao tamanho="pequeno" variante="primaria" onClick={() => definirAberto((x) => !x)}>
        <Plus className="h-4 w-4" /> Destino
      </Botao>
      {aberto ? (
        <>
          <button type="button" className="fixed inset-0 z-30 cursor-default" aria-label="Fechar" onClick={() => definirAberto(false)} />
          <ul className="absolute right-0 z-40 mt-1 max-h-80 w-64 overflow-auto rounded-xl border border-borda bg-superficie p-1 shadow-media">
            {destinos.map((d) => {
              const Icone = d.tipo === 'FULL' ? Store : Warehouse;
              return (
                <li key={d.id}>
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[13px] hover:bg-superficie2"
                    onClick={() => {
                      aoEscolher(d.id);
                      definirAberto(false);
                    }}
                  >
                    <Icone className="h-4 w-4 text-primaria" />
                    <span className="min-w-0 flex-1">
                      <b>{d.nome}</b> {d.uf}
                      <span className="block text-2xs text-textoSuave">
                        empresa {d.empresa} · {d.tipo === 'FULL' ? 'Full' : 'armazém'}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      ) : null}
    </div>
  );
}

function EstadoSalvo({ salvando, sujo, erro, id, encerrada }: { salvando: boolean; sujo: boolean; erro: unknown; id: number | null; encerrada: boolean }): JSX.Element | null {
  if (encerrada) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-status-livreFundo px-2 py-0.5 text-2xs font-semibold text-status-livre">
        <CheckCircle2 className="h-3 w-3" /> Carga {id} concluída
      </span>
    );
  }
  if (erro) return <span className="text-2xs text-status-divergencia">{erro instanceof ErroHttp ? erro.message : 'Não salvou'}</span>;
  if (salvando || sujo) {
    return (
      <span className="inline-flex items-center gap-1 text-2xs text-textoSuave">
        <Loader2 className="h-3 w-3 animate-spin" /> salvando…
      </span>
    );
  }
  if (!id) return null;
  return (
    <span className="inline-flex items-center gap-1 text-2xs text-textoSuave" title="O rascunho se salva sozinho; outra pessoa pode abrir e continuar">
      <CheckCircle2 className="h-3.5 w-3.5 text-status-livre" /> carga {id} salva
    </span>
  );
}

function Vazio(): JSX.Element {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
      <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primaria-suave text-primaria">
        <PackageOpen className="h-6 w-6" />
      </span>
      <div className="max-w-md space-y-1">
        <p className="text-sm font-bold">Monte a carga em três passos</p>
        <ol className="space-y-1 text-left text-[13px] text-textoFraco">
          <li>
            <b className="text-texto">1.</b> Marque à esquerda as NFs de compra que vão no caminhão (aparece o saldo de cada uma).
          </li>
          <li>
            <b className="text-texto">2.</b> Adicione os destinos e distribua: digite, use <b>%</b> do saldo, <b>resto</b> ou <b>nota inteira →</b>.
          </li>
          <li>
            <b className="text-texto">3.</b> Confira a NF de cada destino, embaixo, e emita. O que não foi distribuído fica de saldo.
          </li>
        </ol>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ coluna esquerda

function CargasSalvas({
  cargas,
  carregando,
  atual,
  destinos,
  aoAbrir,
  aoNova,
}: {
  cargas: ResumoCarga[];
  carregando: boolean;
  atual: number | null;
  destinos: Destino[];
  aoAbrir: (c: ResumoCarga) => void;
  aoNova: () => void;
}): JSX.Element | null {
  const cliente = useQueryClient();
  const cancelar = useMutation({
    mutationFn: (id: number) => api.del(`/api/entradas/cargas/${id}`),
    // Descartar libera o que o rascunho tinha distribuído: notas, grade e prévia recalculam.
    onSuccess: () => {
      for (const k of ['entradas-cargas', 'entradas-carga-notas', 'entradas-carga-itens', 'entradas-carga-previa']) void cliente.invalidateQueries({ queryKey: [k] });
    },
  });
  return (
    <Cartao className="shrink-0 overflow-hidden">
      <div className="flex items-center justify-between border-b border-borda px-3 py-2">
        <p className="text-xs font-bold">Cargas</p>
        <Botao tamanho="micro" variante="suave" onClick={aoNova}>
          <Plus className="h-3.5 w-3.5" /> Nova
        </Botao>
      </div>
      {cargas.length ? (
        <ul className="max-h-60 space-y-1.5 overflow-auto p-2">
          {cargas.map((c) => {
            const nomes = c.plano.destinos.map((d) => destinos.find((x) => x.id === d.destinoId)).filter(Boolean);
            const aberta = c.id === atual;
            const un = c.plano.destinos.reduce((s, d) => s + d.linhas.reduce((t, l) => t + l.quantidade, 0), 0);
            return (
              <li key={c.id} className="group relative">
                {/* O cartão inteiro é o botão de abrir: borda, sombra e seta dizem que é clicável. */}
                <button
                  type="button"
                  onClick={() => aoAbrir(c)}
                  aria-current={aberta ? 'true' : undefined}
                  className={cn(
                    'flex w-full items-center gap-2.5 rounded-lg border px-2.5 py-2 text-left transition',
                    aberta
                      ? 'border-primaria bg-primaria-suave/60 shadow-xs ring-1 ring-primaria/30'
                      : 'border-borda bg-superficie hover:-translate-y-px hover:border-primaria/50 hover:shadow-media',
                  )}
                >
                  <span
                    className={cn(
                      'flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-2xs font-bold tabular-nums',
                      aberta ? 'bg-primaria text-white' : 'bg-superficie2 text-textoFraco group-hover:bg-primaria-suave group-hover:text-primaria',
                    )}
                  >
                    #{c.id}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-semibold">{c.nome}</span>
                    <span className="block truncate text-2xs text-textoSuave">
                      {c.plano.compras.length} nota(s) · {n0(un)} un. → {nomes.map((d) => `${d!.nome} ${d!.uf}`).join(', ') || 'sem destino'}
                    </span>
                    {/* Quem está montando: para tirar dúvida com a pessoa antes de mexer no rascunho dela. */}
                    <span
                      className="block truncate text-2xs text-textoSuave"
                      title={`Criada por ${c.criadoPor} em ${new Date(c.criadoEm).toLocaleString('pt-BR')}${c.atualizadoPor ? ` · última alteração por ${c.atualizadoPor}${c.atualizadoEm ? ` em ${new Date(c.atualizadoEm).toLocaleString('pt-BR')}` : ''}` : ''}`}
                    >
                      por <b className="font-semibold text-textoFraco">{c.criadoPor}</b>
                      {c.atualizadoPor && c.atualizadoPor !== c.criadoPor ? (
                        <>
                          {' '}· mexida por <b className="font-semibold text-textoFraco">{c.atualizadoPor}</b>
                        </>
                      ) : null}
                      {' '}· {haQuanto(c.atualizadoEm ?? c.criadoEm)}
                    </span>
                  </span>
                  <span
                    className={cn(
                      'shrink-0 rounded-full px-1.5 py-0.5 text-2xs font-semibold',
                      c.status === 'CONCLUIDA' ? 'bg-status-livreFundo text-status-livre' : c.status === 'EMITINDO' ? 'bg-status-atencaoFundo text-status-atencao' : 'bg-superficie2 text-textoFraco',
                    )}
                  >
                    {c.status === 'RASCUNHO' ? 'rascunho' : c.status === 'EMITINDO' ? `${c.nfs.filter((x) => x.status !== 'ERRO' && x.status !== 'CANCELADA').length}/${c.plano.destinos.length} NF` : 'concluída'}
                  </span>
                  {aberta ? (
                    <span className="shrink-0 text-2xs font-semibold text-primaria">aberta</span>
                  ) : (
                    <span className="flex shrink-0 items-center gap-0.5 text-2xs font-semibold text-primaria opacity-70 group-hover:opacity-100">
                      abrir <ChevronRight className="h-3.5 w-3.5" />
                    </span>
                  )}
                </button>
                {c.status === 'RASCUNHO' ? (
                  <button
                    type="button"
                    className="absolute -right-1 -top-1 hidden h-5 w-5 items-center justify-center rounded-full border border-borda bg-superficie text-textoSuave shadow-xs hover:border-status-divergencia hover:text-status-divergencia group-hover:flex"
                    title={`Descartar o rascunho #${c.id}`}
                    onClick={() => {
                      if (c.id === atual) aoNova();
                      cancelar.mutate(c.id);
                    }}
                  >
                    <X className="h-3 w-3" />
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : carregando ? (
        <div className="p-3">
          <Esqueleto className="h-8" />
        </div>
      ) : (
        <p className="px-3 py-2 text-2xs text-textoSuave">Nenhuma carga aberta. Comece marcando as notas abaixo.</p>
      )}
    </Cartao>
  );
}

function Notas({
  escolhidas,
  empresa,
  aoAlternar,
  travado,
  cargaId,
}: {
  escolhidas: number[];
  empresa: number | null;
  aoAlternar: (n: NotaParaCarga) => void;
  travado: boolean;
  cargaId: number | null;
}): JSX.Element {
  const [busca, definirBusca] = React.useState('');
  const buscaAtrasada = useAtrasado(busca.trim(), 300);
  const q = useQuery<{ notas: NotaParaCarga[] }>({
    queryKey: ['entradas-carga-notas', buscaAtrasada, cargaId],
    queryFn: () => {
      const q = new URLSearchParams();
      if (buscaAtrasada) q.set('busca', buscaAtrasada);
      if (cargaId) q.set('carga', String(cargaId));
      return api.get(`/api/entradas/carga/notas${q.size ? `?${q}` : ''}`);
    },
    placeholderData: (a) => a,
  });
  const notas = q.data?.notas ?? [];
  // Ordem fixa: reordenar ao marcar faz o próximo clique cair na nota errada.
  const ordenadas = notas;
  return (
    <Cartao className="flex min-h-0 flex-col overflow-hidden lg:flex-1">
      <div className="shrink-0 space-y-2 border-b border-borda px-3 py-2">
        <div className="flex items-center justify-between">
          <p className="text-xs font-bold" title="Só aparecem notas com saldo na NF E estoque no ERP para sair">
            NFs de compra com saldo
          </p>
          <span className="text-2xs text-textoSuave">{escolhidas.length ? `${escolhidas.length} marcada(s)` : `${notas.length} nota(s)`}</span>
        </div>
        <label className="flex items-center gap-2 rounded-lg border border-borda bg-superficie px-2">
          <Search className="h-3.5 w-3.5 text-textoSuave" />
          <input className="h-8 w-full bg-transparent text-xs focus:outline-none" placeholder="Fornecedor, NF, NF de compra ou produto" value={busca} onChange={(e) => definirBusca(e.target.value)} />
          {q.isFetching ? <Loader2 className="h-3.5 w-3.5 animate-spin text-textoSuave" /> : null}
        </label>
      </div>
      <ul className="max-h-[52vh] divide-y divide-borda overflow-y-auto overscroll-contain lg:max-h-none lg:min-h-0 lg:flex-1">
        {q.isLoading ? (
          <li className="p-3">
            <Esqueleto className="h-10" />
          </li>
        ) : ordenadas.length ? (
          ordenadas.map((n) => {
            const marcada = escolhidas.includes(n.nfCompra);
            const outraEmpresa = empresa != null && n.empresa !== empresa && !marcada;
            const pct = n.unidades ? (n.saldo / n.unidades) * 100 : 0;
            return (
              <li key={n.chave}>
                <label
                  className={cn(
                    'flex cursor-pointer gap-2.5 px-3 py-2 transition-colors',
                    marcada ? 'bg-primaria-suave/60' : 'hover:bg-superficie2',
                    (outraEmpresa || travado) && 'cursor-not-allowed opacity-50',
                  )}
                  title={outraEmpresa ? `Empresa ${n.empresa}: uma carga sai de uma empresa só (${empresa}).` : undefined}
                >
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 accent-[rgb(var(--cor-primaria))]"
                    checked={marcada}
                    disabled={outraEmpresa || travado}
                    onChange={() => aoAlternar(n)}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-baseline justify-between gap-2">
                      <b className="truncate text-[13px]">{caixaAlta(n.emitente) ?? '—'}</b>
                      <span className="shrink-0 text-2xs text-textoSuave">{n.emissao ? new Date(n.emissao).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : ''}</span>
                    </span>
                    <span className="block text-2xs text-textoFraco">
                      NF {n.numero} · {n.itens} item(ns) · {moeda(n.valor)} · emp. {n.empresa}
                    </span>
                    {n.produtos.length ? (
                      <span className="mt-1 block space-y-0.5">
                        {n.produtos.slice(0, 3).map((p) => (
                          <span key={p.produto} className="flex items-baseline gap-1.5 rounded bg-primaria-suave/60 px-1.5 py-0.5 text-2xs">
                            <b className="tabular-nums text-primaria">{p.produto}</b>
                            <span className="min-w-0 flex-1 truncate text-textoFraco">{p.descricao}</span>
                            <span className="shrink-0 tabular-nums font-semibold">{n0(p.saldo)} un.</span>
                          </span>
                        ))}
                        {n.produtos.length > 3 ? <span className="text-2xs text-textoSuave">+ {n.produtos.length - 3} item(ns)</span> : null}
                      </span>
                    ) : null}
                    <span className="mt-1 flex items-center gap-2">
                      <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-superficie2">
                        <span className="block h-full rounded-full bg-status-livre" style={{ width: `${n.unidades ? (Math.min(n.saldo, n.disponivel) / n.unidades) * 100 : pct}%` }} />
                      </span>
                      <span className="shrink-0 text-2xs tabular-nums text-textoFraco" title="Disponível (estoque do ERP) / unidades da nota">
                        {n0(Math.min(n.saldo, n.disponivel))}/{n0(n.unidades)} un.
                      </span>
                    </span>
                    {n.emOutrasCargas > 0 ? (
                      <span
                        className={cn('mt-0.5 block text-2xs font-semibold', n.emOutrasCargas >= n.saldo ? 'text-status-divergencia' : 'text-status-atencao')}
                        title="Rascunhos não reservam: quem emitir primeiro leva. Confira antes de distribuir."
                      >
                        {n0(n.emOutrasCargas)} un. já em outra carga aberta{n.emOutrasCargas >= n.saldo ? ' — sem sobra' : ` · livre ${n0(n.saldo - n.emOutrasCargas)}`}
                      </span>
                    ) : null}
                  </span>
                </label>
              </li>
            );
          })
        ) : (
          <li className="px-3 py-4 text-xs text-textoFraco">{busca ? 'Nada com essa busca.' : 'Nenhuma NF de compra com saldo a transferir.'}</li>
        )}
      </ul>
    </Cartao>
  );
}

// ------------------------------------------------------------------ NFs da carga

function NfsDaCarga({
  previa,
  carregando,
  erro,
  cargaId,
  sujo,
  aoEmitida,
}: {
  previa: PreviaCarga | undefined;
  carregando: boolean;
  erro: unknown;
  cargaId: number | null;
  sujo: boolean;
  aoEmitida: () => void;
}): JSX.Element {
  if (erro && !previa) {
    return <p className="rounded-lg bg-status-atencaoFundo px-3 py-2 text-[13px] text-status-atencao">{erro instanceof ErroHttp ? erro.message : 'Não deu para calcular as NFs.'}</p>;
  }
  if (!previa) {
    return (
      <div className="grid gap-3 xl:grid-cols-2">
        <Esqueleto className="h-48" />
        <Esqueleto className="h-48" />
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <p className="rotulo">NFs de transferência desta carga</p>
        {carregando ? <Loader2 className="h-3.5 w-3.5 animate-spin text-textoSuave" aria-label="Recalculando" /> : null}
        {!previa.ligada ? <span className="text-2xs text-textoSuave">(emissão pelo WMS desligada no Cadastro: é só a prévia)</span> : null}
      </div>
      {previa.estoque.map((m) => (
        <p key={m} className="flex gap-2 rounded-lg bg-status-divergenciaFundo px-3 py-2 text-[13px] text-status-divergencia">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          Somando os destinos: {m}
        </p>
      ))}
      {previa.conflitos.length ? (
        <p className="flex gap-2 rounded-lg bg-status-atencaoFundo px-3 py-2 text-[13px] text-status-atencao">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {previa.conflitos.length} item(ns) também estão em outra carga aberta e, somando, passam do saldo (ex.: produto {previa.conflitos[0]!.produto}: {n0(previa.conflitos[0]!.aqui)} aqui +{' '}
            {n0(previa.conflitos[0]!.outras)} na carga {previa.conflitos[0]!.cargas.join(', ')} de {n0(previa.conflitos[0]!.saldo)}). Rascunho não reserva: a primeira a emitir leva, a outra é
            barrada. Ajuste ou descarte a outra carga.
          </span>
        </p>
      ) : null}
      {previa.excedidos.length ? (
        <p className="flex gap-2 rounded-lg bg-status-divergenciaFundo px-3 py-2 text-[13px] text-status-divergencia">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          {previa.excedidos.length} item(ns) distribuídos acima do saldo (ex.: produto {previa.excedidos[0]!.produto}: {n0(previa.excedidos[0]!.distribuido)} de{' '}
          {n0(previa.excedidos[0]!.saldo)}). Ajuste na grade — a coluna “Fica” mostra em vermelho.
        </p>
      ) : null}
      <div className="grid gap-3 xl:grid-cols-2">
        {previa.destinos.map((p) => (
          <CartaoNf key={p.destino.id} p={p} cargaId={cargaId} podeEmitir={previa.podeEmitir} sujo={sujo} excedeu={previa.excedidos.length > 0} estoqueGeral={previa.estoque.length > 0} aoEmitida={aoEmitida} />
        ))}
      </div>
    </div>
  );
}

function CartaoNf({
  p,
  cargaId,
  podeEmitir,
  sujo,
  excedeu,
  estoqueGeral,
  aoEmitida,
}: {
  p: PreviaCarga['destinos'][number];
  cargaId: number | null;
  podeEmitir: boolean;
  sujo: boolean;
  excedeu: boolean;
  estoqueGeral: boolean;
  aoEmitida: () => void;
}): JSX.Element {
  const [confirmando, definirConfirmando] = React.useState(false);
  const [aberto, definirAberto] = React.useState(false);
  const emitir = useMutation({
    mutationFn: () => api.post<{ numero: number | null }>(`/api/entradas/cargas/${cargaId}/emitir/${p.destino.id}`, { totalConferido: p.total }),
    onSuccess: aoEmitida,
    onSettled: () => definirConfirmando(false),
  });
  const bloqueado = p.bloqueios.length > 0 || excedeu || estoqueGeral;
  const notas = new Set(p.itens.map((i) => i.nfCompra)).size;
  const un = p.itens.reduce((s, i) => s + i.quantidade, 0);
  return (
    <Cartao className={cn('space-y-3 p-4', p.emitida && 'border-status-livre/50')}>
      <Rota
        compacta
        origem={{ empresa: p.empresaOrigem, fantasia: p.origem.fantasia, uf: p.ufOrigem, centro: p.centroSaida, centroNome: p.origem.centroNome }}
        destino={{ empresa: p.destino.empresa, fantasia: p.destino.fantasia, nome: p.destino.nome, uf: p.destino.uf, tipo: p.destino.tipo, centro: p.destino.centro, centroNome: p.destino.centroNome }}
        legenda={`op. ${p.operacao} → ${p.operacaoEntrada} · série ${p.serie}`}
      />
      <dl className="grid grid-cols-4 gap-2 text-xs">
        {(
          [
            ['Valor', moeda(p.total)],
            ['ICMS', moeda(p.icmsTotal)],
            ['Unidades', `${n0(un)} · ${p.itens.length} item(ns)`],
            ['Peso', `${n0(Math.round(p.pesoTotal))} kg`],
          ] as const
        ).map(([k, v]) => (
          <div key={k} className="min-w-0 rounded-lg bg-superficie2 px-2 py-1.5">
            <dt className="text-2xs font-semibold uppercase tracking-wide text-textoSuave">{k}</dt>
            <dd className="truncate font-semibold tabular-nums">{v}</dd>
          </div>
        ))}
      </dl>
      <button type="button" className="text-2xs font-semibold text-textoFraco hover:text-texto" onClick={() => definirAberto((x) => !x)}>
        {aberto ? 'Esconder' : 'Ver'} os itens ({notas} nota{notas > 1 ? 's' : ''} de compra)
      </button>
      {aberto ? (
        <table className="w-full text-2xs">
          <tbody>
            {p.itens.map((i) => (
              <tr key={i.nfCompraProduto} className="border-t border-borda">
                <td className="py-1">
                  <b className="tabular-nums">{i.produto}</b> <span className="text-textoSuave">NF {i.numeroCompra}</span>
                </td>
                <td className="py-1 text-right tabular-nums">{n0(i.quantidade)} ×</td>
                <td className="py-1 text-right tabular-nums">{i.valorUnitario?.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}</td>
                <td className="py-1 text-right text-textoSuave">
                  CST {i.cst ?? '—'} · {i.aliquota ?? '—'}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {!p.emitida
        ? p.bloqueios.map((b) => (
            <p key={b} className="rounded-lg bg-status-divergenciaFundo px-2.5 py-1.5 text-xs text-status-divergencia">
              {b}
            </p>
          ))
        : null}
      {!p.emitida
        ? p.avisos.map((a) => (
            <p key={a} className="rounded-lg bg-status-atencaoFundo px-2.5 py-1.5 text-2xs text-status-atencao">
              {a}
            </p>
          ))
        : null}
      {p.emitida ? (
        <p className="flex items-center gap-2 rounded-lg bg-status-livreFundo px-3 py-2 text-[13px] font-semibold text-status-livre">
          <CheckCircle2 className="h-4 w-4" /> NF {p.emitida.numero ?? '…'} emitida · {p.emitida.status.toLowerCase()}
        </p>
      ) : !podeEmitir ? (
        <p className="text-2xs text-textoSuave">Emite quem agenda ou o fiscal. Esta é a NF que vai sair.</p>
      ) : confirmando ? (
        <div className="space-y-2 rounded-lg border border-primaria/40 bg-primaria-suave/40 p-3 text-[13px]">
          <p>
            Emitir a NF de <b>{moeda(p.total)}</b> para <b>{p.destino.nome} {p.destino.uf}</b>? Ela é numerada, baixa o estoque do centro {p.centroSaida} e vai para a
            SEFAZ. Cancelamento só pelo Procfit.
          </p>
          <div className="flex justify-end gap-2">
            <Botao tamanho="pequeno" variante="fantasma" disabled={emitir.isPending} onClick={() => definirConfirmando(false)}>
              Voltar
            </Botao>
            <Botao tamanho="pequeno" variante="primaria" carregando={emitir.isPending} onClick={() => emitir.mutate()}>
              <Check className="h-4 w-4" /> Sim, emitir
            </Botao>
          </div>
        </div>
      ) : (
        <Botao
          variante="primaria"
          className="w-full"
          disabled={bloqueado || sujo || !cargaId}
          title={sujo ? 'Esperando o rascunho salvar' : undefined}
          onClick={() => definirConfirmando(true)}
        >
          <FileOutput className="h-4 w-4" />
          {bloqueado ? 'Resolva o que está em vermelho' : sujo ? 'Salvando…' : `Emitir NF para ${p.destino.nome} ${p.destino.uf}`}
        </Botao>
      )}
      {emitir.error ? (
        <p className="rounded-lg bg-status-divergenciaFundo px-2.5 py-1.5 text-xs text-status-divergencia">
          {emitir.error instanceof ErroHttp ? emitir.error.message : String(emitir.error)}
        </p>
      ) : null}
    </Cartao>
  );
}
